import { memo, useCallback, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';
import { calculateDownPayment } from '@atc/wallet-payment-fns';

import {
    Box,
    Heading,
    Section,
} from 'reaxl';
import { sendClick, sendImpressions } from 'reaxl-analytics';
import {
    inventoryVehicleProtectionImpression,
    psxVDPVHRImpression,
} from 'reaxl-analytics-handlers';
import { getAppliedDealerSettings } from 'reaxl-wallet';

import { fallbackInterestRates } from 'axl-config';

import configureExperianTile from '@/utilities/configureExperianTile';
import getPsxVendor from '@/utilities/getPsxVendor';

import { inventoryAdditionalProtectionsImpression, inventoryHistoryReportImpressions, inventoryRecallDetailsClick, inventoryRecallImpression } from '@/analytics/vdp/inventoryProductEvents';
import { inventoryWarrantyDetailClick, inventoryWarrantyDetailImpression } from '@/analytics/vdp/inventoryVehicleDetailsEvents';

import authDuck from '@/ducks/authDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import interestRateDuck from '@/ducks/interestRateDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import userDuck from '@/ducks/userDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import AdditionalProtectionCard from './AdditionalProtectionCard';
import GridComponent from './GridComponent';
import MoneyBackGuaranteeCard from './MoneyBackGuaranteeCard';
import PsxAdditionalProtectionCard from './PsxAdditionalProtectionCard';
import PsxWarrantyCard from './PsxWarrantyCard';
import PurchaseWithConfidenceModal from './PurchaseWithConfidenceModal';
import SafeGuardCard from './SafeGuardCard';
import SafetyRecallCard from './SafetyRecallCard';
import VhrCard from './VhrCard';
import WarrantyCard from './WarrantyCard';

/**
 * PurchaseWithConfidenceContainer - Main container for vehicle purchase confidence features
 *
 * This component renders a comprehensive set of purchase confidence cards including warranties,
 * vehicle history reports, money-back guarantees, safety recalls, and additional protections.
 * It dynamically shows/hides cards based on available data and feature flags, supports both
 * regular dealers and private sellers (PSX), and handles email lead gate functionality.
 *
 * @param {Object} props - Component props
 * @param {Object} [props.emailCTAProps={}] - Configuration for email CTA components
 * @param {Function} [props.onEmailCTAClick] - Callback for email CTA interactions in lead gate mode
 *
 * @returns {JSX.Element|null} The purchase confidence section or null if no content is available
 */
function PurchaseWithConfidenceContainer({
    emailCTAProps = {},
    onEmailCTAClick = () => { },
}) {

    const {
        enable_vhr_lead_gate: [enableVhrLeadGate],
        override_experian_tile: [overrideExperianTile],
        peace_of_mind: [, { theme: peaceOfMindTheme }],
        remove_carfax_tile: [removeCarfaxTile],
        vdp_safeguard: [isSafeGuardEnabled],
        vdp_additional_protection: [isAdditionalProtection],
        vdp_enable_vhr_overview: [enableVhrOverview],
    } = useFeatures([
        'enable_vhr_lead_gate',
        'override_experian_tile',
        'peace_of_mind',
        'remove_carfax_tile',
        'vdp_safeguard',
        'vdp_additional_protection',
        'vdp_enable_vhr_overview',
    ]);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const listingData = useMemo(() => {
        if (!listing) return {};

        const {
            warranties: warranty = {},
            owner: {
                hideEmailForm = false,
                privateSeller = false,
            } = {},
            paymentServices: {
                digitalRetailingType = '',
            } = {},
            vhrTiles = [],
            vhrPreview = [],
            id: listingId = '',
            type: listingType = '',
            vin = '',
            moneyBackGuarantee = null,
            returnPolicy = null,
            safeGuardProgram = null,
            safetyRecall = null,
            fbaCarfaxReport = null,
        } = listing;

        return {
            warranty,
            hideEmailForm,
            privateSeller,
            digitalRetailingType,
            vhrTiles,
            vhrPreview,
            listingId,
            listingType,
            vin,
            moneyBackGuarantee,
            returnPolicy,
            safeGuardProgram,
            safetyRecall,
            fbaCarfaxReport,
        };
    }, [listing]);

    const {
        warranty,
        hideEmailForm,
        privateSeller,
        digitalRetailingType,
        vhrTiles,
        vhrPreview,
        listingId,
        listingType,
        vin,
        moneyBackGuarantee,
        returnPolicy,
        safeGuardProgram,
        safetyRecall,
    } = listingData;

    const calculatedValues = useMemo(() => {
        const isDRAccelerate = digitalRetailingType === 'accelerate';
        const isPeaceOfMindThemeInverse = peaceOfMindTheme === 'inverse';
        const showMoneyBack = returnPolicy?.hasMoneyBackGuarantee || returnPolicy?.hasVehicleReturnPolicy || moneyBackGuarantee;

        return {
            isDRAccelerate,
            isPeaceOfMindThemeInverse,
            showMoneyBack,
        };
    }, [digitalRetailingType, peaceOfMindTheme, returnPolicy?.hasMoneyBackGuarantee, returnPolicy?.hasVehicleReturnPolicy, moneyBackGuarantee]);

    const { isDRAccelerate, isPeaceOfMindThemeInverse, showMoneyBack } = calculatedValues;

    const dealDetails = useSelector(dealsDuck.selectors.getTransformedDealDetails);
    const existingDeal = useSelector((state) => dealsDuck.selectors.getDealByListingVin(state, vin));
    const nationalRates = useSelector(interestRateDuck.selectors.getDuckState);
    const paymentsInfo = useSelector(paymentsDuck.selectors.getPaymentsInfo);
    const walletInteractions = useSelector((_) => paymentsDuck.selectors.getMyWalletInteractions(paymentsInfo));
    const filterValues = useSelector(srpFiltersDuck.selectors.getRequestValues);
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);

    const [modalDetail, setModalDetail] = useState({
        title: '',
        isShow: false,
        content: '',
        size: '',
        footer: '',
        action: '',
    });

    const { brand } = useBrand();

    const { id: dealXgId } = existingDeal ?? {};
    const isExistingDeal = !!dealXgId;
    const { vehicleProtection: { products, VPPSelectedFlag } = {} } = dealDetails?.[dealXgId] || {};
    const hasVehicleProtection = VPPSelectedFlag && (products?.length > 0);

    const { paymentServices = {}, pricingDetail: { salePrice, msrp } = {} } = listing;
    const { dealerSettings = {} } = paymentServices;
    const price = salePrice || msrp;
    const { interactions: { paymentEmphasis } = {} } = paymentsInfo;

    const {
        defaultLeaseDownPayment,
        downPayment,
        downPaymentPercent,
    } = getAppliedDealerSettings({
        dealerSettings,
        walletInteractions: walletInteractions || {},
        price,
        payments: paymentsInfo,
        nationalRates: nationalRates || fallbackInterestRates,
    });

    const handleSeeDetailsClick = useCallback((event, { warrantyType }) => {
        sendClick(inventoryWarrantyDetailClick, event, { warrantyType, listingType, inventoryId: listingId });
    }, [listingType, listingId]);

    const handleRecallImpression = useCallback(() => {
        sendImpressions({ name: inventoryRecallImpression, data: { listingType, inventoryId: listingId } });
    }, [listingType, listingId]);

    const handleRecallDetailsClick = useCallback((event) => {
        sendClick(inventoryRecallDetailsClick, event, { listingType, inventoryId: listingId });
    }, [listingType, listingId]);

    const handleWarrantyInViewImpressions = useCallback((warrantyType) => {
        sendImpressions({ name: inventoryWarrantyDetailImpression, data: { warrantyType, listingType, inventoryId: listingId } });
    }, [listingType, listingId]);

    const handleProtectionImpression = useCallback(() => {
        sendImpressions({ name: inventoryAdditionalProtectionsImpression, data: { listingType, inventoryId: listingId } });
    }, [listingType, listingId]);

    const handleVehicleProtectionImpression = useCallback(() => {
        sendImpressions({ name: inventoryVehicleProtectionImpression, data: { listingType, inventoryId: listingId } });
    }, [listingType, listingId]);

    const handleVhrInviewImpression = useCallback((tile) => {
        if (privateSeller) {
            sendImpressions({ name: psxVDPVHRImpression, data: { listingId, vendor: getPsxVendor(tile.tileType) } });
        } else {
            const cmp = tile?.tileType?.split('_')[0] === 'CARFAX' ? 'carfaxvhr' : 'autocheck';
            sendImpressions({ name: inventoryHistoryReportImpressions, data: { cmp, inventoryId: listingId } });
        }
    }, [listingId, privateSeller]);

    const warrantyInfo = useMemo(() => {
        if (!warranty) return { hasLimitedWarranty: false, hasFactoryWarranty: false, hasBatteryWarranty: false };

        const { limitedWarranty, vehicleCoverage, originalWarranties = {}, originalWarrantyExpired = true } = warranty;
        const limitedWarrantyData = limitedWarranty || vehicleCoverage;
        const { hybridElectricComponents, hybridElectricComponentsInCarbState, ...factoryWarranties } = originalWarranties;

        const hasBatteryWarranty = !!hybridElectricComponents || !!hybridElectricComponentsInCarbState;
        const hasLimitedWarranty = !!(limitedWarrantyData && Object.keys(limitedWarrantyData).length > 0);
        const hasFactoryWarranty = !!(factoryWarranties && Object.keys(factoryWarranties).length > 0) && !originalWarrantyExpired;

        return {
            originalWarranties,
            hasBatteryWarranty,
            hasLimitedWarranty,
            hasFactoryWarranty,
        };
    }, [warranty]);

    const {
        originalWarranties,
        hasBatteryWarranty,
        hasLimitedWarranty,
        hasFactoryWarranty,
    } = warrantyInfo;

    const digitalRetailProps = useMemo(() => ({
        listedPrice: price,
        listing,
        paymentsInfo: {
            ...paymentsInfo,
            dueAtSigning: defaultLeaseDownPayment || calculateDownPayment(price, {
                downPayment: paymentEmphasis ? downPayment : defaultLeaseDownPayment,
                downPaymentPercent: paymentEmphasis ? downPaymentPercent : 0.10,
            }),
        },
        paymentsDuck,
        userDuck,
        isLoggedIn,
    }), [price, listing, paymentsInfo, defaultLeaseDownPayment, paymentEmphasis, downPayment, downPaymentPercent, isLoggedIn]);

    const vhrRenderData = useMemo(() => {
        const hasVhrTiles = Array.isArray(vhrTiles) && vhrTiles.length > 0;
        const hasVhrPreview = Array.isArray(vhrPreview) && vhrPreview.length > 0;

        if (!hasVhrTiles && !hasVhrPreview) return null;

        const tilesToRender = hasVhrTiles ? vhrTiles : [null];

        return {
            hasVhrTiles,
            hasVhrPreview,
            tilesToRender,
        };
    }, [vhrTiles, vhrPreview]);

    // Generate VHR cards if we have data
    const vhrCards = useMemo(() => {
        if (!vhrRenderData) return [];

        const { hasVhrTiles, tilesToRender } = vhrRenderData;

        return tilesToRender.map((tile, index) => {
            // Only process if we have tiles, or provide empty object for preview-only scenarios
            const updatedTile = hasVhrTiles && tile ? configureExperianTile(brand, tile) : {};

            return (
                <VhrCard
                    key={`vhr-${index}`}
                    borderRadius="inset"
                    index={index}
                    isInverse={isPeaceOfMindThemeInverse}
                    isPsx={privateSeller}
                    vhrTile={updatedTile}
                    vhrPreview={vhrPreview}
                    listingId={listingId}
                    handleContentImpression={handleVhrInviewImpression}
                    overrideExperianTile={overrideExperianTile}
                    removeVhrCarfaxTile={removeCarfaxTile}
                    shouldRedirectToExperianPage={enableVhrOverview}
                    {...(enableVhrLeadGate && !hideEmailForm && { onEmailCTAClick })}
                />
            );
        });
    }, [vhrRenderData, brand, isPeaceOfMindThemeInverse, privateSeller, vhrPreview, listingId, handleVhrInviewImpression, overrideExperianTile, removeCarfaxTile, enableVhrLeadGate, hideEmailForm, onEmailCTAClick, enableVhrOverview]);

    const purchaseConfidenceItems = useMemo(() => {
        const items = [];

        // Add warranty cards
        if (hasFactoryWarranty || hasLimitedWarranty || hasBatteryWarranty) {
            if (privateSeller && hasFactoryWarranty) {
                items.push(
                    <PsxWarrantyCard
                        key="psx_warranty"
                        borderRadius="inset"
                        warranty={originalWarranties}
                        listingId={listingId}
                    />
                );
            } else {
                items.push(
                    <WarrantyCard
                        key="warranty"
                        borderRadius="inset"
                        warranty={warranty}
                        handleSeeDetailsClick={handleSeeDetailsClick}
                        handleContentImpression={handleWarrantyInViewImpressions}
                        hasBatteryWarranty={hasBatteryWarranty}
                        hasFactoryWarranty={hasFactoryWarranty}
                        hasLimitedWarranty={hasLimitedWarranty}
                        isSafeGuardEnabled={isSafeGuardEnabled}
                        listingId={listingId}
                        isInverse={isPeaceOfMindThemeInverse}
                    />
                );
            }
        }

        // Add SafeGuard card
        if (safeGuardProgram) {
            items.push(
                <SafeGuardCard
                    key="safeguard"
                    borderRadius="inset"
                    safeGuardProgram={safeGuardProgram}
                    setModalDetail={setModalDetail}
                    handleSeeDetailsClick={handleSeeDetailsClick}
                    handleContentImpression={handleWarrantyInViewImpressions}
                    listingId={listingId}
                    isInverse={isPeaceOfMindThemeInverse}
                />
            );
        }

        // Add Money Back Guarantee card
        if (showMoneyBack) {
            items.push(
                <MoneyBackGuaranteeCard
                    key="mbg"
                    borderRadius="inset"
                    returnPolicy={returnPolicy}
                    setModalDetail={setModalDetail}
                    handleSeeDetailsClick={handleSeeDetailsClick}
                    handleContentImpression={handleWarrantyInViewImpressions}
                    listingId={listingId}
                    isInverse={isPeaceOfMindThemeInverse}
                />
            );
        }

        // Add VHR cards
        items.push(...vhrCards);

        // Add protection cards
        if (privateSeller) {
            items.push(
                <PsxAdditionalProtectionCard
                    key="psx_protections"
                    borderRadius="inset"
                    listingId={listingId}
                    filterValues={filterValues}
                />
            );
        } else if (isAdditionalProtection && (!isDRAccelerate || !isExistingDeal || !hasVehicleProtection)) {
            items.push(
                <AdditionalProtectionCard
                    borderRadius="inset"
                    emailCTAProps={emailCTAProps}
                    handleContentImpression={handleProtectionImpression}
                    handleVehicleProtectionImpression={handleVehicleProtectionImpression}
                    hideEmailForm={hideEmailForm}
                    isInverse={isPeaceOfMindThemeInverse}
                    key="protections"
                    listingId={listingId}
                    listingType={listingType}
                    onEmailCTAClick={onEmailCTAClick}
                    setModalDetail={setModalDetail}
                    {...(isDRAccelerate && { digitalRetailProps })}
                />
            );
        }

        // Add safety recall card
        if (safetyRecall) {
            items.push(
                <SafetyRecallCard
                    key="recall"
                    borderRadius="inset"
                    safetyRecall={safetyRecall}
                    handleRecallDetailsClick={handleRecallDetailsClick}
                    handleContentImpression={handleRecallImpression}
                    listingId={listingId}
                    isInverse={isPeaceOfMindThemeInverse}
                />
            );
        }

        return items;
    }, [
        digitalRetailProps, emailCTAProps, filterValues, handleProtectionImpression, handleRecallDetailsClick,
        handleRecallImpression, handleSeeDetailsClick, handleVehicleProtectionImpression, handleWarrantyInViewImpressions,
        hasBatteryWarranty, hasFactoryWarranty, hasLimitedWarranty, hasVehicleProtection, hideEmailForm, isAdditionalProtection,
        isDRAccelerate, isExistingDeal, isPeaceOfMindThemeInverse, isSafeGuardEnabled, listingId,
        listingType, onEmailCTAClick, originalWarranties, privateSeller, returnPolicy, safeGuardProgram, safetyRecall,
        setModalDetail, showMoneyBack, vhrCards, warranty,
    ]);

    const mainContent = useMemo(() => (
        <>
            <Heading
                componentClass="h3"
                className={clsx({
                    'margin-bottom-4 text-inverse': isPeaceOfMindThemeInverse,
                    'margin-bottom-2': !isPeaceOfMindThemeInverse,
                })}
                level={3}
            >
                Have peace of mind when you buy
            </Heading>
            <GridComponent
                isPsx={privateSeller}
                data-cmp="purchase-confidence-grid-container"
            >
                {purchaseConfidenceItems}
            </GridComponent>
        </>
    ), [isPeaceOfMindThemeInverse, privateSeller, purchaseConfidenceItems]);

    if (!purchaseConfidenceItems.length) {
        return null;
    }

    return (
        <Section
            alignment="left"
            id="purchaseConfidence"
            className="padding-vertical-4 padding-vertical-md-5"
        >
            {isPeaceOfMindThemeInverse ? (
                <Box
                    backgroundColor="brand-inverse"
                    textColor="inverse"
                    className="padding-5"
                >
                    {mainContent}
                </Box>
            ) : mainContent}
            {modalDetail.isShow
                && (
                    <PurchaseWithConfidenceModal
                        {...modalDetail}
                        setModalDetail={setModalDetail}
                    />
                )}
        </Section>
    );
}

PurchaseWithConfidenceContainer.propTypes = {
    /** Configuration object for email CTA components */
    emailCTAProps: PropTypes.object,

    /** Callback function for email CTA interactions when lead gate features are enabled */
    onEmailCTAClick: PropTypes.func,
};

export default memo(PurchaseWithConfidenceContainer);
